import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import DashboardLayout from "../components/DashboardLayout";
import { toast } from "sonner";
import { Copy, Sparkle, Clock, FilmStrip } from "@phosphor-icons/react";

export default function Studio() {
  const [collages, setCollages] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [active, setActive] = useState(null); // active photo
  const [loading, setLoading] = useState(true);

  // AI generator state
  const [aiTopic, setAiTopic] = useState("");
  const [aiFormat, setAiFormat] = useState("Reel");
  const [aiDuration, setAiDuration] = useState(45);
  const [aiNotes, setAiNotes] = useState("");
  const [aiResult, setAiResult] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/persona/collages"), api.get("/persona/photos")])
      .then(([c, p]) => {
        setCollages(c.data.collages || []);
        setPhotos(p.data || []);
        if (p.data && p.data.length) setActive(p.data[0]);
      })
      .catch(() => toast.error("Failed to load content studio"))
      .finally(() => setLoading(false));
  }, []);

  const copy = (text, label = "Script") => {
    navigator.clipboard.writeText(text);
    toast.success(`Copied: ${label}`);
  };

  const generate = async () => {
    if (!aiTopic.trim()) {
      toast.error("Enter a topic");
      return;
    }
    setAiLoading(true);
    setAiResult(null);
    try {
      const r = await api.post("/ai/generate-script", {
        topic: aiTopic,
        format: aiFormat,
        duration_sec: Number(aiDuration),
        extra_notes: aiNotes,
      });
      setAiResult(r.data);
      toast.success("Script generated");
    } catch (e) {
      toast.error("Generation failed");
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-10">
        <p className="eyebrow"><span className="gold-divider" />Studio</p>
        <h1 className="font-display text-4xl mt-3">Content Studio</h1>
        <p className="text-sm text-exodus-muted mt-2 max-w-3xl">
          Every photo of Leon is paired with a custom 45–60 second first-person script you can record over with AI voice cloning. Tap any tile to load its script.
        </p>

        {loading ? (
          <p className="text-exodus-muted mt-10">Loading studio…</p>
        ) : (
          <div className="mt-10 grid lg:grid-cols-12 gap-8">
            {/* LEFT: Collage browser */}
            <div className="lg:col-span-7 space-y-12">
              {collages.map((collage) => {
                const tiles = photos.filter((p) => p.collage_id === collage.id);
                return (
                  <div key={collage.id} data-testid={`collage-${collage.id}`}>
                    <div className="flex items-end justify-between mb-4">
                      <div>
                        <p className="eyebrow text-xs">{collage.id.replace("-", " ")}</p>
                        <h3 className="font-display text-2xl mt-1">{collage.title}</h3>
                        {collage.summary && (
                          <p
                            className="text-xs text-exodus-muted mt-1 max-w-xl leading-relaxed"
                            data-testid={`collage-summary-${collage.id}`}
                          >
                            {collage.summary}
                          </p>
                        )}
                      </div>
                      <span className="text-xs text-exodus-muted font-mono">
                        {tiles.length} tiles
                      </span>
                    </div>
                    <div className="relative bg-exodus-surface border border-exodus-border p-2">
                      <img
                        src={collage.url}
                        alt={collage.title}
                        className="w-full block"
                        loading="lazy"
                      />
                      {/* tile overlay grid */}
                      <div
                        className="absolute inset-2 grid pointer-events-none"
                        style={{
                          gridTemplateColumns: `repeat(${collage.grid.cols}, minmax(0,1fr))`,
                          gridTemplateRows: `repeat(${collage.grid.rows}, minmax(0,1fr))`,
                        }}
                      >
                        {tiles.map((p) => (
                          <button
                            key={p.id}
                            data-testid={`tile-${p.id}`}
                            onClick={() => setActive(p)}
                            className={`pointer-events-auto border transition-all relative group ${
                              active?.id === p.id
                                ? "border-exodus-gold shadow-[inset_0_0_0_2px_rgba(197,160,89,0.6)]"
                                : "border-transparent hover:border-exodus-gold/60"
                            }`}
                            aria-label={p.topic}
                          >
                            <span className="absolute inset-0 tile-overlay opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-2">
                              <span className="text-[10px] text-exodus-gold tracking-wider uppercase font-medium leading-tight text-left">
                                {p.topic}
                              </span>
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* RIGHT: Active script + AI generator (sticky) */}
            <aside className="lg:col-span-5 space-y-6 lg:sticky lg:top-6 self-start">
              {active && (
                <div data-testid="active-script-panel" className="card-surface p-7">
                  <p className="eyebrow text-xs">{active.collage_title}</p>
                  <h3 className="font-display text-3xl mt-2 leading-tight">{active.topic}</h3>
                  <div className="flex items-center gap-4 mt-3 text-xs text-exodus-muted">
                    <span className="flex items-center gap-1"><Clock size={12} /> {active.duration_sec}s</span>
                    <span className="flex items-center gap-1"><FilmStrip size={12} /> Reel / Short</span>
                  </div>
                  <div className="mt-6 pb-4 border-b border-exodus-border">
                    <p className="eyebrow text-xs mb-2">Hook</p>
                    <p className="font-display text-lg italic">"{active.hook}"</p>
                  </div>
                  <div className="mt-5">
                    <p className="eyebrow text-xs mb-3">Script</p>
                    <p className="text-sm leading-relaxed text-exodus-text/90 whitespace-pre-line">{active.script}</p>
                  </div>
                  <button
                    data-testid="copy-script-button"
                    onClick={() => copy(active.script, active.topic)}
                    className="btn-gold w-full justify-center mt-6"
                  >
                    Copy Script <Copy size={16} weight="bold" />
                  </button>
                </div>
              )}

              {/* AI generator */}
              <div data-testid="ai-generator-panel" className="card-surface p-7">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkle size={18} weight="fill" className="text-exodus-gold" />
                  <p className="eyebrow text-xs">AI Script Generator</p>
                </div>
                <h3 className="font-display text-2xl">Generate a custom script.</h3>
                <p className="text-xs text-exodus-muted mt-1">Powered by Claude Sonnet 4.5 · branded for EXODUS.</p>

                <div className="mt-5 space-y-3">
                  <input
                    data-testid="ai-topic-input"
                    className="input-exodus"
                    placeholder="Topic — e.g., 'Why Roth conversions hurt high earners'"
                    value={aiTopic}
                    onChange={(e) => setAiTopic(e.target.value)}
                  />
                  <div className="grid grid-cols-2 gap-3">
                    <select
                      data-testid="ai-format-select"
                      className="input-exodus"
                      value={aiFormat}
                      onChange={(e) => setAiFormat(e.target.value)}
                    >
                      <option>Reel</option>
                      <option>Cold DM</option>
                      <option>Pitch</option>
                      <option>Objection Handler</option>
                      <option>Follow-up Email</option>
                    </select>
                    <select
                      data-testid="ai-duration-select"
                      className="input-exodus"
                      value={aiDuration}
                      onChange={(e) => setAiDuration(e.target.value)}
                    >
                      <option value={30}>30 sec</option>
                      <option value={45}>45 sec</option>
                      <option value={60}>60 sec</option>
                      <option value={90}>90 sec</option>
                    </select>
                  </div>
                  <textarea
                    data-testid="ai-notes-input"
                    className="input-exodus"
                    placeholder="Optional notes — angle, audience, tone…"
                    rows={2}
                    value={aiNotes}
                    onChange={(e) => setAiNotes(e.target.value)}
                  />
                  <button
                    data-testid="ai-generate-button"
                    onClick={generate}
                    disabled={aiLoading}
                    className="btn-gold w-full justify-center"
                  >
                    {aiLoading ? "Generating…" : "Generate Script"}
                    {!aiLoading && <Sparkle size={14} weight="fill" />}
                  </button>
                </div>

                {aiResult && (
                  <div data-testid="ai-result" className="mt-6 pt-6 border-t border-exodus-border space-y-4">
                    {aiResult.hook && (
                      <div>
                        <p className="eyebrow text-xs mb-1">Hook</p>
                        <p className="font-display text-lg italic">"{aiResult.hook}"</p>
                      </div>
                    )}
                    {aiResult.script && (
                      <div>
                        <p className="eyebrow text-xs mb-1">Script</p>
                        <p className="text-sm leading-relaxed text-exodus-text/90 whitespace-pre-line">{aiResult.script}</p>
                      </div>
                    )}
                    {aiResult.cta && (
                      <div>
                        <p className="eyebrow text-xs mb-1">CTA</p>
                        <p className="text-sm text-exodus-gold">{aiResult.cta}</p>
                      </div>
                    )}
                    <button
                      onClick={() =>
                        copy(
                          `${aiResult.hook}\n\n${aiResult.script}\n\n${aiResult.cta}`.trim(),
                          aiResult.topic
                        )
                      }
                      className="btn-ghost w-full justify-center text-xs"
                      data-testid="copy-ai-script"
                    >
                      Copy Full Script
                    </button>
                  </div>
                )}
              </div>
            </aside>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
